/** @jsxImportSource @emotion/react */
import { useRef, useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@mui/material';
import { SubTitleLayout, SubTitleGroup, ControlBtnGroup } from 'components/layouts/ContentLayout';
import CustomDialog from 'components/modals/common/CustomDialog';
import { IconButton } from 'components/buttons/IconSVG';
import { useMessageBar } from 'hooks/useMessageBar';
import { useLoading } from 'components/process/Loading';
import _ from 'lodash';
import CustomGrid from 'components/grids/CustomGrid';
import { MdIvesDtlIspt } from 'models/md/MdIves';
import { Code } from 'models/common/CommonCode';
import { getCommonCodeNamesCondition } from 'apis/admin/CommonCode';
import { CrudCode } from 'models/common/Edit';
import * as input from '@grapecity/wijmo.input';
import { DataMap } from '@grapecity/wijmo.grid';

type Props = {
  open: boolean;
  close: () => void;
  callbackFn: (selectedItems: any) => void;
};

/**
 * 상세 점검사항 추가 팝업
 */
const DtlIsptAddRowPopup = ({ open, close, callbackFn, ...props }: Props) => {
  const { t } = useTranslation();
  const { openMessageBar } = useMessageBar();
  const { openLoading } = useLoading();

  const gridRef = useRef<any>();
  const [count, setCount] = useState(0);
  const [prdnProcCode, setPrdnProcCode] = useState<Code[]>([]);
  const [eqclCode, setEqclCode] = useState<Code[]>([]);
  const [rowData, setRowData] = useState<MdIvesDtlIspt[]>([]);

  const layoutDefinition = useMemo(() => {
    return [
      {
        binding: 'prdnProcCd',
        header: String(t('com.label.공정', '공정')),
        minWidth: 100,
        width: 120,
        cssClass: 'WijmoSelect',
        dataMap: new DataMap(prdnProcCode || [], 'cmnCd', 'cmnCdNm'),
      },
      {
        binding: 'eqclId',
        header: String(t('com.label.설비군', '설비군')),
        minWidth: 140,
        width: 140,
        cssClass: 'WijmoSelect',
        dataMap: new DataMap(eqclCode || [], 'cmnCd', 'cmnCdNm'),
      },
      {
        binding: 'dtlIsptNm',
        header: String(t('com.label.비표준 항목', '비표준 항목')),
        minWidth: 120,
        width: '*',
        align: 'left',
      },
      {
        binding: 'ivesDtlDesc',
        header: String(t('com.label.투자상세', '투자상세')),
        minWidth: 70,
        width: '*',
        align: 'left',
      },
      {
        binding: 'rsnCtn',
        header: String(t('com.label.점검항목', '점검항목')),
        minWidth: 70,
        width: '*',
        align: 'left',
      },
      {
        binding: 'quotUprc',
        header: String(t('com.label.단가', '단가')),
        minWidth: 120,
        width: 120,
        format: 'n7',
        editor: new input.InputNumber(document.createElement('div'), {
          isRequired: false,
          format: 'n7',
        }),
      },
    ];
  }, [rowData]);

  const initCodes = async () => {
    const PRDN_CODE = await getCommonCodeNamesCondition({
      cmnGrCd: 'PRDN_PROC_CD',
      optValCtn7: 'Y',
    });
    setPrdnProcCode(PRDN_CODE);
    const EQCL_ID = await getCommonCodeNamesCondition({
      cmnGrCd: 'MD_EQCL_ID',
    });
    setEqclCode(EQCL_ID);
  };

  const onInitialized = (grid) => {
    gridRef.current = grid;
  };

  const onBeginningEdit = (s, e) => {
    // const item = s.rows[e.row].dataItem;
  };

  const btnAdd = () => {
    const item = {
      _id: '_NEW_' + count,
      prdnProcCd: '',
      eqclId: '',
      dtlIsptNm: '',
      ivesDtlDesc: '',
      rsnCtn: '',
      quotUprc: '',
      crudKey: CrudCode.CREATE,
    };

    setRowData((prev) => [item, ...prev]);
    setCount(count + 1);
  };

  const btnDelete = () => {
    const selectedItems = gridRef.current.rows
      .filter((row) => row.isSelected)
      .map((o) => o.dataItem);
    if (selectedItems.length === 0) {
      openMessageBar({
        type: 'error',
        content: t('com.msg.선택된 데이터가 없습니다.', '선택된 데이터가 없습니다.'),
      });
      return;
    }

    setRowData((prev) => prev.filter((o) => !selectedItems.includes(o)));
  };

  // 한 행에 값이 비어있는지 체크
  const isEmptyObj = (item: any) => {
    return !item.prdnProcCd || !item.eqclId || !item.dtlIsptNm || !item.quotUprc;
  };

  const isValid = () => {
    if (rowData.some((o) => isEmptyObj(o))) {
      return t('com.msg.입력되지 않은 값이 있습니다.', '입력되지 않은 값이 있습니다.');
    } else if (
      rowData.some(
        (o, idx) =>
          idx !==
          rowData.findIndex(
            (x) =>
              x.prdnProcCd === o.prdnProcCd &&
              x.eqclId === o.eqclId &&
              x.dtlIsptNm === o.dtlIsptNm &&
              x.ivesDtlDesc === o.ivesDtlDesc
          )
      )
    ) {
      return t('com.msg.중복된 데이터가 있습니다.', '중복된 데이터가 있습니다.');
    }
  };

  const btnSave = () => {
    if (rowData.length === 0) {
      openMessageBar({
        type: 'error',
        content: t('com.msg.데이터가 없습니다.', '데이터가 없습니다.'),
      });
      return;
    }
    const resultTxt = isValid();
    if (resultTxt) {
      openMessageBar({
        type: 'error',
        content: resultTxt,
      });
      return;
    }

    callbackFn(rowData);
  };

  useEffect(() => {
    initCodes();
  }, []);
  useEffect(() => {
    console.log(rowData);
  }, [rowData]);

  const dialogButtons = [
    <Button
      key={'confirm'}
      css={IconButton.button}
      className="confirm"
      onClick={btnSave}
      disableRipple
    >
      {t('com.button.적용 ', '적용 ')}
    </Button>,
  ];
  return (
    <CustomDialog
      title={String(t('md.label.상세 점검사항 추가', '상세 점검사항 추가'))}
      size={'lg'}
      open={open}
      onClose={close}
      buttons={dialogButtons}
    >
      <SubTitleLayout>
        <SubTitleGroup>
          <h3>{t('md.label.목록', '목록')}</h3>
          <span className="total">
            {t('com.label.총', '총')}
            <span>{(rowData || []).length.toLocaleString()}</span>
            {t('com.label.건', '건')}
          </span>
        </SubTitleGroup>
        <ControlBtnGroup>
          <Button css={IconButton.button} className="addRow" onClick={btnAdd} disableRipple>
            {t('com.button.행추가', '행추가')}
          </Button>
          <Button css={IconButton.button} className="delRow" onClick={btnDelete} disableRipple>
            {t('com.button.행삭제', '행삭제')}
          </Button>
        </ControlBtnGroup>
      </SubTitleLayout>
      <CustomGrid
        layoutDefinition={layoutDefinition}
        rowData={rowData}
        height={500}
        allowPinning={false}
        align="center"
        initialized={onInitialized}
        beginningEdit={onBeginningEdit}
      />
    </CustomDialog>
  );
};

export default DtlIsptAddRowPopup;
